// User activity tracking utilities for personalized content and ad targeting

interface UserActivity {
  contentViews: { [contentId: string]: { count: number; totalTime: number; lastView: number } };
  interactions: { [contentId: string]: { likes: boolean; shares: number; comments: number } };
  interests: { [category: string]: number }; // Category scores based on engagement
  adInteractions: { [adId: string]: { impressions: number; clicks: number; lastSeen: number } };
  searchHistory: string[];
  contentTypePreference: { posts: number; videos: number; reels: number; testimonies: number };
}

const TRACKING_COOKIE = 'belivah_tracking';
const COOKIE_EXPIRY_DAYS = 365;

// Get or initialize user activity profile
export const getUserActivity = (): UserActivity => {
  try {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(TRACKING_COOKIE + '='));
    
    if (cookie) {
      const data = decodeURIComponent(cookie.split('=')[1]);
      return JSON.parse(data);
    }
  } catch (e) {
    console.warn('Failed to parse tracking cookie:', e);
  }

  return {
    contentViews: {},
    interactions: {},
    interests: {},
    adInteractions: {},
    searchHistory: [],
    contentTypePreference: { posts: 0, videos: 0, reels: 0, testimonies: 0 }
  };
};

// Save user activity profile to cookie
const saveUserActivity = (activity: UserActivity) => {
  try {
    const data = JSON.stringify(activity);
    const expires = new Date();
    expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);
    document.cookie = `${TRACKING_COOKIE}=${encodeURIComponent(data)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (e) {
    console.warn('Failed to save tracking cookie:', e);
  }
};

// Track content view
export const trackContentView = (
  contentId: string,
  contentType: 'posts' | 'videos' | 'reels' | 'testimonies',
  category?: string,
  durationSeconds?: number
) => {
  const activity = getUserActivity();
  
  if (!activity.contentViews[contentId]) {
    activity.contentViews[contentId] = { count: 0, totalTime: 0, lastView: Date.now() };
  }
  
  activity.contentViews[contentId].count++;
  activity.contentViews[contentId].totalTime += durationSeconds || 0;
  activity.contentViews[contentId].lastView = Date.now();
  
  // Update content type preference
  activity.contentTypePreference[contentType]++;
  
  // Update interest score for category
  if (category) {
    activity.interests[category] = (activity.interests[category] || 0) + 1;
  }
  
  saveUserActivity(activity);
};

// Track user interaction (like, share, comment)
export const trackInteraction = (
  contentId: string,
  interactionType: 'like' | 'share' | 'comment',
  category?: string
) => {
  const activity = getUserActivity();
  
  if (!activity.interactions[contentId]) {
    activity.interactions[contentId] = { likes: false, shares: 0, comments: 0 };
  }
  
  if (interactionType === 'like') {
    activity.interactions[contentId].likes = true;
  } else if (interactionType === 'share') {
    activity.interactions[contentId].shares++;
  } else if (interactionType === 'comment') {
    activity.interactions[contentId].comments++;
  }
  
  // Strong interest signal - weight higher
  if (category) {
    activity.interests[category] = (activity.interests[category] || 0) + 3;
  }
  
  saveUserActivity(activity);
};

// Track ad impression and clicks
export const trackAdImpression = (adId: string) => {
  const activity = getUserActivity();
  
  if (!activity.adInteractions[adId]) {
    activity.adInteractions[adId] = { impressions: 0, clicks: 0, lastSeen: Date.now() };
  }
  
  activity.adInteractions[adId].impressions++;
  activity.adInteractions[adId].lastSeen = Date.now();
  
  saveUserActivity(activity);
};

export const trackAdClick = (adId: string) => {
  const activity = getUserActivity();
  
  if (!activity.adInteractions[adId]) {
    activity.adInteractions[adId] = { impressions: 0, clicks: 0, lastSeen: Date.now() };
  }
  
  activity.adInteractions[adId].clicks++;
  
  saveUserActivity(activity);
};

// Track search queries
export const trackSearch = (query: string) => {
  const activity = getUserActivity();
  
  activity.searchHistory.push(query.toLowerCase());
  
  // Keep only last 50 searches
  if (activity.searchHistory.length > 50) {
    activity.searchHistory = activity.searchHistory.slice(-50);
  }
  
  saveUserActivity(activity);
};

// Get user's top interests for targeting
export const getTopInterests = (limit: number = 5): string[] => {
  const activity = getUserActivity();
  
  return Object.entries(activity.interests)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([category]) => category);
};

// Get recommended content type
export const getPreferredContentType = (): 'posts' | 'videos' | 'reels' | 'testimonies' => {
  const activity = getUserActivity();
  const prefs = activity.contentTypePreference;
  
  const sorted = Object.entries(prefs).sort(([, a], [, b]) => b - a);
  
  return (sorted[0]?.[0] || 'posts') as 'posts' | 'videos' | 'reels' | 'testimonies';
};

// Check if user has seen ad recently (frequency capping)
export const shouldShowAd = (adId: string, minHoursBetween: number = 24): boolean => {
  const activity = getUserActivity();
  const adData = activity.adInteractions[adId];
  
  if (!adData) return true;
  
  const hoursSinceLastSeen = (Date.now() - adData.lastSeen) / (1000 * 60 * 60);
  return hoursSinceLastSeen >= minHoursBetween;
};

// Get engagement score for a content piece (0-100)
export const getContentEngagementScore = (contentId: string): number => {
  const activity = getUserActivity();
  const view = activity.contentViews[contentId];
  const interaction = activity.interactions[contentId];
  
  if (!view && !interaction) return 0;
  
  let score = 0;
  
  // View count (max 20 points)
  if (view) {
    score += Math.min(view.count * 2, 20);
    
    // Time spent (max 20 points)
    const avgTime = view.totalTime / view.count;
    score += Math.min(avgTime / 3, 20); // 3 seconds = 1 point
  }
  
  // Interactions (max 60 points)
  if (interaction) {
    if (interaction.likes) score += 20;
    score += Math.min(interaction.shares * 15, 30);
    score += Math.min(interaction.comments * 5, 10);
  }
  
  return Math.min(score, 100);
};
